/**
 * MOB search slice — the inputs a skipper sets while working a live man-overboard
 * behind the second press of the MOB button: which casualty type to drift, the
 * wind to drift it with, the fix error for the search radius, and the track
 * spacing, speed and pattern for the search.
 *
 * It's session state, not a saved preference: it belongs to the MOB that's
 * running now and is reset when the MOB is dropped fresh or cleared (see
 * mobActions), so a new emergency never inherits stale numbers from the last one.
 */
import { create } from 'zustand';
import {
  DEFAULT_CASUALTY_ID,
  DEFAULT_IPE_ID,
  DEFAULT_IPE_NM,
  type PatternId,
  type AreaPatternId,
} from '@/safety';

/**
 * Every pattern the MOB sheet can run: the two through-water patterns (steered on
 * a heading and a timer, never plotted) plus the two ground-based area patterns
 * (parallel track / creeping line, run over the ground and plotted on the chart).
 */
export type MobPatternId = PatternId | AreaPatternId;

/**
 * The search-area geometry the map draws, published by the sheet as it recomputes
 * the drift. Plain lon/lat data so the map layer stays a dumb renderer: the datum
 * the circle and box are built on, the search radius, the box ring, and — only
 * for a ground-based area pattern — the plotted track and its commence-search
 * position.
 */
export interface MobSearchOverlay {
  datum: [number, number];
  radiusNm: number;
  /** Closed ring A→B→C→D→A of the search box. */
  boxRing: [number, number][];
  /** The four lettered box corners, for the A/B/C/D labels. */
  boxCorners: { A: [number, number]; B: [number, number]; C: [number, number]; D: [number, number] };
  /** Ground-based area track, or null when a through-water pattern is chosen. */
  area: {
    id: AreaPatternId;
    vertices: [number, number][];
    csp: [number, number];
  } | null;
}

interface MobSearchState {
  /** RNLI leeway-table casualty type id. */
  casualtyId: string;
  setCasualtyId: (id: string) => void;

  /** True = drift with the app's wind; false = use the manual override below. */
  useAppWind: boolean;
  setUseAppWind: (on: boolean) => void;
  /** Manual wind override: direction it blows FROM (°T) and speed (kn). */
  windFromDeg: number;
  windKn: number;
  setWind: (dirFromDeg: number, kn: number) => void;

  /**
   * Initial Position Error for the search radius. `ipeId` is the picked table row
   * (or 'manual' when typed by hand); `ipeNm` is the value in nautical miles that
   * actually feeds the radius. Session-scoped, defaulting to SATNAV / DGPS 0.3 nm.
   */
  ipeId: string;
  ipeNm: number;
  setIpe: (id: string, nm: number) => void;

  /** Track spacing / initial track spacing for the pattern, nm. */
  trackSpacingNm: number;
  setTrackSpacingNm: (nm: number) => void;
  /** Speed to search at, kn (STW for water patterns, SOG for area patterns). */
  patternSpeedKn: number;
  setPatternSpeedKn: (kn: number) => void;

  /** Which pattern is chosen (through-water or ground-based area). */
  patternId: MobPatternId;
  setPatternId: (id: MobPatternId) => void;

  /** When the running pattern was started (for the leg countdown), or null. */
  patternStartedAt: number | null;
  startPattern: () => void;
  stopPattern: () => void;

  /** The search-area geometry currently drawn on the chart, or null. */
  overlay: MobSearchOverlay | null;
  setOverlay: (overlay: MobSearchOverlay | null) => void;

  /** Wipe back to defaults for a fresh MOB. */
  reset: () => void;
}

const DEFAULTS = {
  casualtyId: DEFAULT_CASUALTY_ID,
  useAppWind: true,
  windFromDeg: 270,
  windKn: 10,
  ipeId: DEFAULT_IPE_ID,
  ipeNm: DEFAULT_IPE_NM,
  // 0.4 nm is the RNLI card's worked initial track spacing; a good starting point.
  trackSpacingNm: 0.4,
  // 6 kt is a sensible slow, methodical search speed for a small craft; editable.
  patternSpeedKn: 6,
  patternId: 'expanding_square' as MobPatternId,
  patternStartedAt: null as number | null,
  overlay: null as MobSearchOverlay | null,
};

export const useMobSearchStore = create<MobSearchState>((set) => ({
  ...DEFAULTS,
  setCasualtyId: (casualtyId) => set({ casualtyId }),
  setUseAppWind: (useAppWind) => set({ useAppWind }),
  setWind: (windFromDeg, windKn) => set({ windFromDeg, windKn }),
  setIpe: (ipeId, ipeNm) => set({ ipeId, ipeNm }),
  setTrackSpacingNm: (trackSpacingNm) => set({ trackSpacingNm }),
  setPatternSpeedKn: (patternSpeedKn) => set({ patternSpeedKn }),
  setPatternId: (patternId) => set({ patternId }),
  startPattern: () => set({ patternStartedAt: Date.now() }),
  stopPattern: () => set({ patternStartedAt: null }),
  setOverlay: (overlay) => set({ overlay }),
  reset: () => set({ ...DEFAULTS }),
}));
